import { format, parseISO } from 'date-fns';
import { useCallback, useContext, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerAddDistributionModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddDistributionModal.tsx';
import {
  useIssuerGenerateABAFileDividendMutation,
  useIssuerUpdateStatusDividendEventMutation,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { Button, DisclosureListItem, ModalProviderContext, Tooltip } from 'ui';
import { DownloadGreyIcon, InfoCircleWhiteIcon } from 'ui/icons';
import { DividendEventExtendResponse, DividendStatus, UpdateDividendEventRequest } from 'api';

interface IssuerDividendItemProps {
  dividend: DividendEventExtendResponse;
  index: number;
  isDeleted?: boolean;
}

const IssuerDividendItem = ({ dividend, index, isDeleted = false }: IssuerDividendItemProps) => {
  const { registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { date, amount_per_share, memo, status, register_obj, id } = dividend;
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { mutateAsync: updateDividend, isLoading } = useIssuerUpdateStatusDividendEventMutation();
  const { openModal } = useContext(ModalProviderContext);
  const handleDownload = (base64file?: string) => {
    if (!base64file) return;

    const parsedDate = parseISO(String(date));
    const fileName = `${parsedDate ? format(parsedDate, 'yyyyMMdd') : 'file'}.ABA`;

    const link = document.createElement('a');
    link.href = base64file;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const { mutateAsync: generateABAFile } = useIssuerGenerateABAFileDividendMutation({
    onSuccess: handleDownload,
  });

  const handleUpdateDividend = useCallback(
    (payload: UpdateDividendEventRequest) => {
      updateDividend({
        ...payload,
        register_id: register_obj.id,
      });
    },
    [register_obj.id, updateDividend],
  );

  const continueDistribution = useCallback(() => {
    openModal(IssuerAddDistributionModal, {
      registerCountry,
      register_id: +register_obj.id,
      selectedClass: {
        label: dividend.register_class.class_name,
        value: {
          id: id,
          price_per_share: dividend.register_class.current_price_per_share,
          toString: () => dividend.register_class.class_name,
        },
      },
      modalStep: !dividend.operation_statuses?.payments ? 3 : 4,
      existDividendEventId: dividend.id,
    });
  }, [openModal, registerCountry, register_obj.id, dividend, id]);

  const DividendActions = useMemo(
    () =>
      status === DividendStatus.Pending && (
        <div className="flex items-center justify-start gap-4">
          <Tooltip
            position="down"
            variant="gray"
            button={
              <Button
                className="w-[90px]"
                onClick={() =>
                  handleUpdateDividend({
                    id: id,
                  })
                }
                disabled={isLoading}
                loading={isLoading}
              >
                Finalise
              </Button>
            }
          >
            Press this button after updating the NAV price to calculate the quantity and price for
            all DRP transactions in Dividend Pending status. These will then be moved to Completed
            status.
          </Tooltip>
          <span>
            <Button
              onClick={() => {
                continueDistribution();
              }}
              className="w-[120px] flex gap-2"
              variant="primary"
            >
              <span>Continue</span>
            </Button>
          </span>
        </div>
      ),
    [status, isLoading, handleUpdateDividend, id, continueDistribution],
  );

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [];

    info.push([`Class name`, dividend.register_class.class_name]);

    info.push([
      `${getRegisterTypeStrings('dividend', { capitalize: true })} ID`,
      '#' + dividend.id,
    ]);

    info.push(['$ per share/unit', amount_per_share ? `$${amount_per_share.toString()}` : '-']);

    info.push(['Memo', memo || '-']);

    if ((status === DividendStatus.Pending || registerCountry === 'AUS') && !isDeleted) {
      info.push([
        <div className="flex gap-4 items-center">
          {status === DividendStatus.Pending && <span>{DividendActions}</span>}
          {registerCountry === 'AUS' && (
            <span>
              <Button
                onClick={() => {
                  generateABAFile(id);
                }}
                className="w-[120px] flex gap-2"
                variant="primary"
              >
                <span>ABA File</span>
                <Tooltip
                  position={index === 0 ? 'down' : 'up'}
                  positionHorizontal="right"
                  variant="gray"
                  delayTimeOpen={0}
                  delayTimeClose={0}
                  button={
                    <img
                      alt="info"
                      src={InfoCircleWhiteIcon}
                      className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
                    />
                  }
                >
                  <span>
                    Download .ABA file. You can upload it to your bank to send a payment to every
                    investor who got cash distribution.
                  </span>
                </Tooltip>
              </Button>
            </span>
          )}
        </div>,
        <></>,
      ]);
      //ToDo: add archive button
    }

    return info;
  }, [
    isDeleted,
    dividend.register_class.class_name,
    dividend.id,
    getRegisterTypeStrings,
    amount_per_share,
    memo,
    status,
    registerCountry,
    DividendActions,
    index,
    generateABAFile,
    id,
  ]);

  return (
    <DisclosureListItem
      leftMainString={dividend.name}
      leftSubduedContentSet={[dateToPrint(date) || '-']}
      classes={{ leftContent: 'w-full max-w-[500px] break-all' }}
      rightMainString={`$${formatNumberWithCommas(
        Math.round((dividend.total_amount || 0) * 100) / 100,
      )}`}
      rightContent={
        <div className="flex items-center gap-4">
          {dividend?.reports?.[0]?.file_storage_item_url && (
            <span className="cursor-pointer hover:opacity-80" onClick={e => e.stopPropagation()}>
              <a
                href={dividend?.reports?.[0]?.file_storage_item_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  alt="cerificate"
                  src={DownloadGreyIcon}
                  className="w-6 h-6 min-w-[24px] min-h-[24px]"
                />
              </a>
            </span>
          )}
          <Tooltip
            position="down"
            variant="gray"
            disabled={dividend.status !== DividendStatus.Pending}
            button={
              <ActivityStatusBadge
                className="mr-3 pb-1 pt-1 w-max"
                status={
                  dividend.status == DividendStatus.Pending ? 'DRP Not Finalised' : dividend.status
                }
                isCustomStatus={true}
                info={dividend.status == DividendStatus.Pending}
              />
            }
            classNameTooltip="text-xs"
          >
            Dividends have been distributed and added to holders' total balances, but DRP
            transactions do not yet include quantity and price per share. Click the Finalize button
            after updating the NAV price to move DRPs to Completed status.
          </Tooltip>
        </div>
      }
      rightSubduedString={
        dividend.total_quantity === 0 ||
        !dividend.total_quantity ||
        dividend.status === DividendStatus.Pending ? (
          ''
        ) : (
          <div className="text-nowrap">
            {formatNumberWithCommas(dividend.total_quantity)} units/shares
          </div>
        )
      }
      bottomContent={bottomContentData}
    />
  );
};

export default IssuerDividendItem;
